import { TestLocators } from 'constants/Locators';

import { MUI, ThemeMode, Tooltip } from 'ci-esi-web-shared';
import { debounce } from 'debounce';
import React, {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { Overlay } from 'app/components/common/Overlay';
import { CreateRegex, getValidNumberString, shouldBeShownAsIs } from 'app/components/common/SliderWithTextInput/utils';
import { colors } from 'styles/theme/colors';

import { EAdornmentPlacement } from './types';

export interface SliderWithTextInputProps {
  label: string;
  value: number;
  minValue: number;
  maxValue: number;
  minLabel?: string;
  maxLabel?: string;
  allowFractional?: boolean;
  inputWidth?: string;
  step?: number;
  isLoading?: boolean;
  formatInputValue: (value: number) => string;
  onChange(value: number): void;
  testId?: TestLocators | string;
  disabled?: boolean;
  tooltipText?: string;
  tooltipProps?: Partial<MUI.TooltipProps>;
  maxLength?: number;
  maximumDecimalPlaces?: number | null;
  adornmentSign?: string;
  adornmentPlacement?: EAdornmentPlacement;
}

export type SliderWithTextInputRef = {
  setValue: (value: number) => void;
};

export const SliderWithTextInput = forwardRef(
  (
    {
      label,
      value,
      minValue,
      maxValue,
      minLabel,
      maxLabel,
      allowFractional = false,
      inputWidth = '56px',
      step = 1,
      isLoading,
      formatInputValue,
      onChange,
      testId,
      disabled = false,
      tooltipText = '',
      tooltipProps,
      maxLength = 12,
      maximumDecimalPlaces = null,
      adornmentSign,
      adornmentPlacement,
    }: SliderWithTextInputProps,
    ref: ForwardedRef<SliderWithTextInputRef>,
  ) => {
    const theme = MUI.useTheme();
    const isDarkMode = theme.palette.mode === ThemeMode.DARK;
    const [sliderValue, setSliderValue] = useState<number>(value);
    const [inputValue, setInputValue] = useState<string>(formatInputValue(value));
    const isAnyBorderLabelDefined = maxValue || minLabel;

    useImperativeHandle(
      ref,
      () => ({
        setValue(value: number) {
          setSliderValue(value);
          setInputValue(formatInputValue(value as number));
        },
      }),
      [setSliderValue, setInputValue, formatInputValue],
    );

    const handleSliderChange = useCallback(
      (_event: Event, newValue: number | number[]) => {
        setSliderValue(newValue as number);
        setInputValue(formatInputValue(newValue as number));
      },
      [formatInputValue],
    );

    const handleChange = useCallback(
      (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
        if (typeof newValue === 'number' && newValue >= minValue && newValue <= maxValue) {
          const regex = CreateRegex(maximumDecimalPlaces);
          if (regex.test(newValue.toString())) {
            onChange(newValue);
          }
        }
      },
      [maxValue, minValue, onChange, maximumDecimalPlaces],
    );

    const debouncedHandleChange = useMemo(() => debounce(handleChange, 350), [handleChange]);

    useEffect(() => {
      return () => {
        debouncedHandleChange.clear();
      };
    }, [debouncedHandleChange]);

    const handleTextFieldChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const {
          target: { value },
        } = event;

        const regex = CreateRegex(maximumDecimalPlaces);

        const numberStr = getValidNumberString(value, allowFractional);
        let numberValue = +numberStr;
        if (numberValue > maxValue) {
          numberValue = maxValue;
        }

        if (regex.test(numberValue.toString())) {
          setSliderValue(numberValue < minValue ? minValue : numberValue);
          setInputValue(shouldBeShownAsIs(numberStr, allowFractional) ? numberStr : numberValue.toString());
        }

        debouncedHandleChange(event, numberValue);
      },
      [allowFractional, debouncedHandleChange, maxValue, minValue, maximumDecimalPlaces],
    );

    const handleTextFieldFocus = useCallback(() => {
      setInputValue(getValidNumberString(inputValue, allowFractional));
    }, [inputValue, allowFractional]);

    const handleTextFieldBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setInputValue(formatInputValue(sliderValue));
        if (Number(inputValue) !== sliderValue) {
          handleChange(event, sliderValue);
        }
      },
      [formatInputValue, sliderValue, inputValue, handleChange],
    );

    const positionKey = adornmentPlacement === EAdornmentPlacement.START ? 'start' : 'end';
    const adornmentProps = adornmentSign && {
      [`${positionKey}Adornment`]: (
        <MUI.InputAdornment
          position={positionKey}
          sx={{
            '& > p': {
              color: 'currentColor !important',
              transform: adornmentPlacement === EAdornmentPlacement.START ? 'translateX(3px)' : 'translateX(-3px)',
            },
          }}
        >
          {adornmentSign}
        </MUI.InputAdornment>
      ),
    };

    return (
      <MUI.Box data-testid={testId}>
        <MUI.InputLabel disableAnimation sx={{ transform: 'none' }}>
          {label}
        </MUI.InputLabel>
        <Tooltip {...tooltipProps} title={tooltipText} open={tooltipText ? undefined : false}>
          <MUI.Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              cursor: disabled ? 'not-allowed' : undefined,
            }}
          >
            <MUI.Box
              sx={{
                width: '100%',
                marginRight: '16px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <MUI.Slider
                min={minValue}
                max={maxValue}
                step={step}
                value={sliderValue}
                onChange={handleSliderChange}
                onChangeCommitted={handleChange}
                disabled={disabled}
              />
              {isAnyBorderLabelDefined && (
                <MUI.Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    fontSize: 10,
                    color: isDarkMode ? colors.baseGrey20 : colors.grey45,
                    lineHeight: '10px',
                    marginTop: '2px',
                  }}
                >
                  <MUI.Typography variant="body2" component="div" sx={{ lineHeight: '10px', fontSize: 10 }}>
                    {minLabel}
                  </MUI.Typography>
                  <MUI.Typography
                    variant="body2"
                    component="div"
                    sx={{ marginLeft: 'auto', lineHeight: '10px', fontSize: 10 }}
                  >
                    {maxLabel}
                  </MUI.Typography>
                </MUI.Box>
              )}
            </MUI.Box>
            <MUI.TextField
              inputProps={{ maxLength }}
              onChange={handleTextFieldChange}
              onFocus={handleTextFieldFocus}
              onBlur={handleTextFieldBlur}
              hiddenLabel
              InputProps={{ ...adornmentProps }}
              value={inputValue}
              disabled={disabled}
              sx={{
                width: inputWidth,
                flexShrink: 0,
                marginLeft: 'auto',
                background: isDarkMode ? colors.baseGrey80 : colors.primaryWhite,
                color: 'currentColor',
                '& .MuiInputBase-root': {
                  padding: 0,
                  margin: 0,
                  '& input': {
                    marginLeft: adornmentPlacement === EAdornmentPlacement.START ? '-3px' : '6px',
                  },
                },
              }}
            />
            {isLoading && <Overlay size={20} testId={testId} />}
          </MUI.Box>
        </Tooltip>
      </MUI.Box>
    );
  },
);
